import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDeleteCalendarBlock } from "@/lib/queries";
import { atLeast, type CalendarBlock } from "@/lib/types";

/**
 * One block of time off (ACL-292): whose, when, and a way to remove it. Supervisors remove any; a
 * rep, their own — the same rule core-api holds, so the button is not offered where it would fail.
 */
export function BlockDetailsDialog({ block, onClose }: { block: CalendarBlock | null; onClose: () => void }) {
  const toast = useToast();
  const { user } = useAuth();
  const remove = useDeleteCalendarBlock();
  const canRemove = Boolean(block && (atLeast(user?.role, "supervisor") || block.user_id === user?.id));
  const when = (iso: string) =>
    new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(
      new Date(iso),
    );

  return (
    <Dialog open={block !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{block?.title || "Blocked off time"}</DialogTitle>
        </DialogHeader>
        {block && (
          <DialogBody className="space-y-2 text-sm">
            <p>
              <span className="text-text-muted">For </span>
              {block.user_name ? `${block.user_name}, on every calendar` : `the whole ${block.calendar_name} calendar`}
            </p>
            <p>
              <span className="text-text-muted">From </span>
              {when(block.starts_at)} <span className="text-text-muted">to</span> {when(block.ends_at)}
            </p>
            <p className="text-xs text-text-muted">Nothing can be booked in it; appointments already there stay.</p>
          </DialogBody>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {canRemove && block && (
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() =>
                remove.mutate(block.id, {
                  onSuccess: () => {
                    toast.success("Block removed", "That time can be booked again.");
                    onClose();
                  },
                  onError: (e) => toast.error("Could not remove the block", e instanceof ApiError ? e.message : "Please try again."),
                })
              }
            >
              <Trash2 />
              Remove block
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
